export interface FieldGuideArticle {
  id: string;
  part: number;
  articleNumber: number;
  title: string;
  subtitle?: string;
  content: string;
}

export interface FieldGuidePart {
  number: number;
  title: string;
  subtitle: string;
  articles: FieldGuideArticle[];
}

export const fieldGuideArticles: FieldGuideArticle[] = [
  // Part 1: The Foundation
  {
    id: 'the-bible-glen-vision',
    part: 1,
    articleNumber: 1,
    title: 'The Bible Glen Vision',
    subtitle: 'From Museum to Dig Site',
    content: `You might be wondering, "Why 'Bible Glen'?"

It's a name we chose very intentionally. When you think of a formal museum, you think of a quiet, sterile building where priceless artifacts are kept in glass cases. A museum is where you go to look at the results of a discovery.

But a glen—a quiet, secluded valley—is different. A glen is a place in the field. It's out in the world. It's a place where you can get your hands dirty. It's a place for quiet, focused, personal work.

A museum is where you see the final, polished product. The glen is the dig site.

### Our Field Manual: Hermeneutics
As we dig, we follow a specific "Field Manual" called **Hermeneutics**. It sounds intimidating, but it's simply the "art and science of interpretation." 

An amateur treasure hunter just digs holes randomly, hoping to find something shiny. A Field Archaeologist follows a careful, scientific method. They lay out a grid and use a set of rules to make sure they understand what they've found.

This app is your "Bible Glen." It's not a glass case; it's a field site. Our goal isn't to show you a finished, polished exhibit. Our goal is to hand you a trowel and invite you to start the active, adventurous work of discovery yourself.`
  },
  {
    id: 'unearthing-context',
    part: 1,
    articleNumber: 2,
    title: 'Unearthing the Strata of Context',
    subtitle: 'Understanding the Layers of History',
    content: `As a Field Archaeologist, the single most important lesson is this: **An artifact is meaningless without its context.**

If I find a clay pot, the first question isn't "What is this?" but "Where did it come from?" Was it found in a 1st-century Roman barracks or a 10th-century B.C. Israelite home? The location, the layer, the "stratum" it belongs to—that's what gives it meaning.

## The "Strata" of Scripture

A passage in the Bible works the same way. When we read a verse "in a vacuum," we end up treating a 1st-century Roman coin like a piece from a modern board game—it's the right object in the wrong story. To find the intended meaning, we look through three main layers:

**The Historical Stratum (The "When"):** What was happening in the world? Was it a time of war or peace? A letter written by Paul from a Roman prison (like Philippians) reads very differently when we understand the power of Caesar and the culture of the Empire.

**The Cultural Stratum (The "Who"):** What was life like for the people? Understanding concepts like "honor," "shame," or "hospitality" is like finding a key to the entire house.

**The Literary Stratum (The "How"):** What kind of writing is this? We read a Psalm very differently than we read the book of Leviticus.

## Your Field Gear

This is why Bible Glen provides the tools for the dig. 

- Use the **Dynamic Timeline** to "dig down" through history and see what was happening in Rome, Persia, and Babylon.
- Use the **Interactive Map** to see the "where" of the story and understand the geographical world.
- Explore **Cultural & Historical Notes** to see the significant "artifacts" in the text you might otherwise miss.

Context isn't a "nice-to-have"; it's everything. By unearthing the context, we get one step closer to the profound, authentic meaning of the text.`
  },
  {
    id: 'how-we-use-ai',
    part: 1,
    articleNumber: 3,
    title: 'How We Use AI Responsibly',
    subtitle: 'Understanding Your AI Research Assistant',
    content: `You may have noticed "AI-Curated" badges throughout the app. We believe in total transparency: while the "Field Archaeologist" behind this study is you, much of the foundational context - the timelines, cultural notes, and archaeological data - is curated using Google's Gemini AI.

But this isn't the generic AI you've met elsewhere. We've refined it to act as a precision research tool.

## What Makes This Different

Generic AI tools often "hallucinate" because they are designed to guess the next word in a sentence. Bible Glen’s AI is constrained by a different set of gears.

**We've built a curated scholarly framework.**

Instead of generating answers from thin air, the AI acts as a **scholarly research assistant** with a very specific field manual:

1. **Trusted Sources Only:** The AI is instructed to synthesize data from established academic lexicons (like BDAG and HALOT), standard commentaries (NICNT, Anchor Bible), and primary ancient sources (Josephus, the Dead Sea Scrolls).
2. **A Scholar's Lens:** It is prompted to adopt the meticulous perspective of a biblical researcher, prioritizing linguistic accuracy and historical evidence over generic summaries.
3. **Evidence-Based:** Every claim is paired with a lineage of thought. If the evidence isn't in the scholarly record, it doesn't make the cut.
4. **Structured for Discovery:** Our AI doesn't just "chat." It organizes its findings into structured schemas (Maps, Timelines, Artifact Cards) designed to help you make your own discoveries.

## Why We Use AI

Meaningful biblical study shouldn't be gated behind a $2,000 library or a decade of graduate school. To provide this depth for every verse in the Bible would normally require an army of scholars working for years. 

AI allows us to democratize the discovery process. It is a high-performance tool—much like a precision laser scanner at a dig site—that helps you uncover layers of reality that were once hidden behind language and time.

## Our Theological Compass

While Bible Glen is a tool for exploration, it is anchored in a clear set of convictions:

**Respecting the Text:** We believe the Bible is the inspired, authoritative Word of God. Our guide is prompted to treat the text with that high regard, prioritizing historically orthodox and conservative interpretations that affirm its redemptive message.

**The Unity of Truth:** We welcome the insights of archaeology, linguistics, and history. We use the tools of scholarship to gather data, then view that data through a lens of faithful, historical orthodoxy.

## How You Can Verify

We don't expect you to outsource your thinking. Bible Glen is designed to empower your discernment, not replace it:

1. **Check the Sources:** Every discovery card includes a "Sources" list. We encourage you to look them up.
2. **Compare the Work:** Cross-reference our findings with your own trusted commentaries and study tools.
3. **Help Us Refine:** If you spot an error or a biased interpretation, report it. Each report helps the guide become sharper for the whole community.

## The Bottom Line

The AI isn't here to do the thinking for you. It’s here to hand you the shovel, clear away the dust of 2,000 years, and point you toward the most promising strata of the text. The discovery itself is yours to make.`
  },
  // Part 2: Practical Discovery
  {
    id: 'the-head-heart-hands-method',
    part: 2,
    articleNumber: 4,
    title: 'The "Head, Heart, Hands" Method',
    subtitle: 'From Ancient Text to Modern Life',
    content: `An archaeologist's work isn't done until they've built a bridge. The final question is: "What does this ancient discovery mean for us today?"

To avoid pulling 3,000-year-old artifacts into our world without understanding them, we follow a two-step "bridge-building" process:

**Step 1: Understand Then.** We first use our tools to understand what the text meant to its original audience. What was the author saying? This is the solid foundation of our bridge.

**Step 2: Apply Now.** Once we've grasped the timeless principle inside its original cultural package, we build the bridge to our world.

### A Holistic Engagement
Your engagement with Scripture should engage your entire person:

**Engage the HEAD (Understand & Internalize)**
This is the intellectual part of the dig. Use the app to summarize passages and wrestle with the scholarly context.

**Engage the HEART (Reflect & Pray)**
This is the emotional and spiritual part. Connect with the meaning behind the "artifacts" you uncover through reflection and prayer.

**Engage the HANDS (Apply & Act)**
This is the physical "so what?" What is one concrete thing you can do this week based on what you've unearthed?

By engaging your Head, Heart, and Hands, you ensure that the text you're unearthing isn't just an ancient artifact you admire. It's a living word that has the power to shape your mind, move your heart, and guide your hands.`
  },
  {
    id: 'our-core-convictions',
    part: 1,
    articleNumber: 4,
    title: 'Our Core Convictions',
    subtitle: 'The Compass of the Glen',
    content: `BibleGlen is anchored in a set of core convictions—a "compass" that guides our research and your discovery. We believe that faithful study is not just about gathering data, but about encountering the living God in His Word.

### 1. Audience Relevance
The Bible was written **FOR** us, but not **TO** us. Recovering the original context isn't a secret formula, but a lens that brings the author's intent into focus—deepening our understanding and grounding our application.

### 2. Christ the Center
Jesus is the center and climax of the biblical narrative. Every passage participates in the unfolding story of God's redemptive work, finding its ultimate fulfillment in Him.

### 3. Grounded Integrity
Rigorous historical and linguistic research serves as a steward of the truth, ensuring our interpretations are anchored in the objective reality of the biblical world rather than modern assumptions.

### 4. The Great Cloud
Scripture is a gift to the whole people of God. Engaging with the global and historical church protects us from individual bias and exposes our own cultural blind spots.

### 5. Literary Design
Scripture is a masterpiece of intentional design by both human and divine authors. We honor the text by respecting the unique artistry and "rules" of its various genres, from poetry to narrative.

### 6. Formation over Information
The end of all biblical study is the love of God and neighbor. Technical mastery of the ancient world serves a higher purpose: to encounter the living God and be transformed into the image of Christ.

### 7. The Unity of Truth
All truth is God’s truth. We do not fear the discoveries of archaeology, history, or science, but welcome them as partners that help us better understand the reality the Bible describes.

### 8. Prayerful Dependency
While scholarship provides the data, the Holy Spirit provides the illumination. We pair rigorous study with prayerful humility, trusting the Spirit to speak truthfully to the heart of every seeker.`
  }
];

export const fieldGuideParts: FieldGuidePart[] = [
  {
    number: 1,
    title: 'The Foundation',
    subtitle: 'Methods of the Dig',
    articles: fieldGuideArticles.filter(a => a.part === 1)
  },
  {
    number: 2,
    title: 'Practical Discovery',
    subtitle: 'Building the Bridge',
    articles: fieldGuideArticles.filter(a => a.part === 2)
  }
];

export function getArticleById(id: string): FieldGuideArticle | undefined {
  return fieldGuideArticles.find(article => article.id === id);
}

export function getArticlesByPart(part: number): FieldGuideArticle[] {
  return fieldGuideArticles.filter(article => article.part === part);
}

